import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import { useApiContext } from '@/contexts/apiContext';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataSourceItems from '../dataSource/dataSourceItems';

type Props = {
  userAppInstance: any;
  maxHeight: number;
  onOpenDataSourceItem: (item: any) => void;
};

const StyledListItem = styled(ListItemButton)(({ theme, selected }) => ({
  textTransform: 'none',
  borderLeft: selected ? '1px solid' : '',
  borderColor: selected ? theme.palette.secondary.main : '',
}));

const DataSources = ({ userAppInstance, maxHeight, onOpenDataSourceItem }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [dataSources, setDataSources] = useState<any>([]);
  const [selectedDataSource, setSelectedDataSource] = useState<any>();
  const [error, setError] = useState<any>();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const getDataSources = async () => {
      setError(null);
      try {
        setLoading(true);
        const dataSources = await apiContext.getUserAppInstanceDataSources(
          userAppInstance.userAppDefId,
          userAppInstance.id,
        );
        setDataSources(dataSources);
        if (dataSources.length > 0) {
          setSelectedDataSource(dataSources[0]);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (userAppInstance) {
      getDataSources();
    }
  }, [userAppInstance]);

  const handleChange = (dataSource: any) => {
    setSelectedDataSource(dataSource);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const renderSidebarContent = () => (
    <Box sx={{ width: isMobile ? '240px' : '200px', p: 1 }}>
      <List component="nav" aria-label="main mailbox folders">
        {dataSources.map((ds: any) => (
          <ListItem key={ds.id} dense disablePadding sx={{ maxWidth: '100%' }}>
            <StyledListItem
              selected={selectedDataSource?.id === ds.id}
              title={ds.name}
              onClick={() => handleChange(ds)}
              sx={{ width: '100%' }}
            >
              <ListItemText
                primaryTypographyProps={{ noWrap: true, style: { textOverflow: 'ellipsis' } }}
                primary={ds.name}
                secondary={ds.dataSourceDef?.name}
              />
            </StyledListItem>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  if (loading) {
    return <ProgressIndicator />;
  }

  return (
    <Stack direction={'row'} sx={{ height: '100%', maxHeight: maxHeight, borderTop: 1, borderColor: 'divider' }}>
      {!isMobile && dataSources.length > 0 && (
        <Stack justifyContent="space-between" alignItems="center" sx={{ borderRight: 1, borderColor: 'divider', flexShrink: 0 }}>
          {renderSidebarContent()}
        </Stack>
      )}

      {isMobile && (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
        >
          {renderSidebarContent()}
        </Drawer>
      )}

      <Box
        role="tabpanel"
        sx={{
          height: '100%',
          flexGrow: 1,
          p: 0.5,
          px: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isMobile && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ py: 1, borderBottom: 1, borderColor: 'divider', mb: 1, flexShrink: 0 }}
          >
            <IconButton onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Data Sources ({selectedDataSource?.name || 'None'})
            </Typography>
          </Stack>
        )}
        {error && <ErrorMessage error={error} />}
        {dataSources.length === 0 && <Typography sx={{ p: 2 }}>No Datasources</Typography>}
        {selectedDataSource && (
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <DataSourceItems
              dataSourceInstance={selectedDataSource}
              onOpenDataSourceItem={onOpenDataSourceItem}
              maxHeight={isMobile ? maxHeight - 56 : maxHeight}
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default DataSources;
